// Image compression utility for reducing file size before upload
import heic2any from 'heic2any';

/**
 * Convert HEIC/HEIF to JPEG
 * Uses heic2any library for reliable conversion across all browsers
 */
async function convertHeicToJpeg(file) {
  try {
    console.log(`[HEIC] Converting ${file.name}...`);
    
    // Convert HEIC to JPEG blob
    const jpegBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 0.85
    });
    
    // Handle case where heic2any returns array (multiple images)
    const blob = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
    
    // Create new file with .jpg extension
    const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
    const convertedFile = new File([blob], newName, {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
    
    console.log(`[HEIC] Converted ${file.name} → ${newName} (${(convertedFile.size / 1024).toFixed(0)}KB)`);
    return convertedFile;
    
  } catch (error) {
    console.error(`[HEIC] Conversion failed for ${file.name}:`, error);
    
    // Fallback: try native browser conversion
    try {
      return await convertToJpegNative(file);
    } catch (e) {
      console.error('[HEIC] Native fallback also failed:', e);
      throw new Error(`Δεν ήταν δυνατή η μετατροπή του αρχείου ${file.name}. Δοκιμάστε να το μετατρέψετε σε JPEG πρώτα.`);
    }
  }
}

/**
 * Native browser conversion fallback (works in Safari)
 */
async function convertToJpegNative(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, img.width, img.height);
      ctx.drawImage(img, 0, 0);
      
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to create blob'));
            return;
          }
          const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
          const convertedFile = new File([blob], newName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });
          resolve(convertedFile);
        },
        'image/jpeg',
        0.85
      );
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    
    const url = URL.createObjectURL(file);
    img.src = url;
  });
}

/**
 * Check if file is HEIC/HEIF format
 */
function isHeicFile(file) {
  const ext = file.name.split('.').pop().toLowerCase();
  return ext === 'heic' || ext === 'heif' || 
         file.type === 'image/heic' || file.type === 'image/heif' ||
         file.type === '';  // iOS sometimes doesn't set type for HEIC
}

/**
 * Compress an image file using canvas
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxWidth - Maximum width (default: 1920)
 * @param {number} options.maxHeight - Maximum height (default: 1920)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.7)
 * @returns {Promise<File>} - Compressed image file
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = 1920,
    maxHeight = 1920,
    quality = 0.7
  } = options;

  // Convert HEIC/HEIF to JPEG first
  if (isHeicFile(file)) {
    console.log(`[Compression] Detected HEIC/HEIF: ${file.name}`);
    try {
      file = await convertHeicToJpeg(file);
    } catch (error) {
      console.error('[Compression] HEIC conversion failed:', error);
      throw error;
    }
  }

  // Skip compression for non-image files
  if (!file.type.startsWith('image/')) {
    console.warn(`[Compression] Skipping non-image: ${file.name} (${file.type})`);
    return file;
  }

  // Skip compression for small files (< 500KB)
  if (file.size < 500 * 1024) {
    console.log(`[Compression] Skipping ${file.name} - already small (${(file.size / 1024).toFixed(1)}KB)`);
    return file;
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Calculate new dimensions while maintaining aspect ratio
      let { width, height } = img;
      
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw image on canvas
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            console.error('[Compression] Failed to create blob');
            resolve(file); // Return original on error
            return;
          }

          // Ensure filename ends with .jpg
          let fileName = file.name;
          if (!fileName.toLowerCase().endsWith('.jpg') && !fileName.toLowerCase().endsWith('.jpeg')) {
            fileName = fileName.replace(/\.[^.]+$/, '.jpg');
          }

          // Create new file with compressed data
          const compressedFile = new File([blob], fileName, {
            type: 'image/jpeg',
            lastModified: Date.now()
          });

          const originalSize = file.size / 1024;
          const compressedSize = compressedFile.size / 1024;
          const savings = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
          
          console.log(`[Compression] ${file.name}: ${originalSize.toFixed(1)}KB → ${compressedSize.toFixed(1)}KB (${savings}% saved)`);

          resolve(compressedFile);
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = (error) => {
      console.error('[Compression] Failed to load image:', error);
      resolve(file); // Return original on error
    };

    // Load image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = (error) => {
      console.error('[Compression] Failed to read file:', error);
      resolve(file);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Compress multiple image files
 * @param {FileList|File[]} files - Array of files to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File[]>} - Array of compressed files
 */
export async function compressImages(files, options = {}) {
  const fileArray = Array.from(files);
  const results = [];
  
  for (const file of fileArray) {
    try {
      const compressed = await compressImage(file, options);
      results.push(compressed);
    } catch (error) {
      console.error(`[Compression] Failed to process ${file.name}:`, error);
      // Don't add failed files, but don't stop processing
    }
  }
  
  return results;
}
